// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  FedExConfirmation,
  FedExDropLocation,
  FedExError,
  FedExPickUpAddress,
  FedExShipperDetails,
  ProposedFedExPickupTime,
} from "./FedExTypes";
import React, { useCallback, useEffect, useReducer } from "react";
import { confirmPickup, useFedExState } from "store/fedex";
import { setScheduledPickup, useTestRunState } from "store/testrun";
import { useHistory, useParams } from "react-router-dom";

import FedExAddressForm from "./FedExAddressForm";
import FedExConfirmationPage from "./FedExConfirmation";
import FedExSelectAddress from "./FedExSelectAddress";
import FedExSelectTime from "./FedExSelectTime";
import { useAppDispatch } from "store";

type FedExShippingStateType =
  | "ENTER_ADDRESS"
  | "SELECT_TIME"
  | "SELECT_ADDRESS"
  | "CONFIRMATION";

const FEDEX_SHIPPING_PAGES: FedExShippingStateType[] = [
  "ENTER_ADDRESS",
  "SELECT_ADDRESS",
  "SELECT_TIME",
  "CONFIRMATION",
];

interface FedExShippingData {
  address?: FedExPickUpAddress;
  addressError?: FedExError;
  shipperDetails?: FedExShipperDetails;
  proposedTimes?: Array<ProposedFedExPickupTime>;
  proposedAddresses?: {
    original: FedExPickUpAddress;
    normalized: FedExPickUpAddress;
  };
}

interface FedExShippingAction {
  data?: FedExShippingData;
}

export interface FedExShippingState {
  data: FedExShippingData;
}

const fedExShippingReducer = (
  state: FedExShippingState,
  action: FedExShippingAction
): FedExShippingState => {
  return {
    data: {
      ...state.data,
      ...action.data,
    },
  };
};

const getPage = (
  pageNumber: number,
  state: FedExShippingState,
  confirmation: FedExConfirmation | undefined
): FedExShippingStateType => {
  let page: FedExShippingStateType = FEDEX_SHIPPING_PAGES[pageNumber];
  // Navigating back to this component after scheduling pickup always shows
  // the confirmation page
  if (confirmation) {
    return "CONFIRMATION";
  }

  // If somebody navigates directly to a page the necessary state won't be
  // available, so jump back to the address entry page
  if (page === "CONFIRMATION" && !confirmation) {
    page = "SELECT_TIME";
  }
  if (page === "SELECT_TIME" && !state.data.proposedTimes) {
    page = "SELECT_ADDRESS";
  }
  if (page === "SELECT_ADDRESS" && !state.data.proposedAddresses) {
    page = "ENTER_ADDRESS";
  }
  return page;
};

export default () => {
  const { page: pageNumberStr } = useParams() as {
    page: string;
    testRunUID: string;
  };
  const pageNumber = parseInt(pageNumberStr) || 0;
  const [shippingState, dispatch] = useReducer(fedExShippingReducer, {
    data: {},
  });
  const { confirmation: confirmationDetails } = useFedExState();
  const appDispatch = useAppDispatch();
  const history = useHistory();
  const testRunState = useTestRunState();

  const confirmation = testRunState?.testRun?.scheduledPickup;

  const page = getPage(pageNumber, shippingState, confirmation);

  useEffect(() => {
    if (page !== "CONFIRMATION" && page !== FEDEX_SHIPPING_PAGES[pageNumber]) {
      history.push(`./${FEDEX_SHIPPING_PAGES.indexOf(page)}`);
    }
  }, [history, page, pageNumber]);

  const onReceivePickupTimes = useCallback(
    (data: {
      proposedTimes: Array<ProposedFedExPickupTime>;
      shipperDetails: FedExShipperDetails;
      address: FedExPickUpAddress;
    }) => {
      history.push("./2");
      dispatch({ data });
    },
    [history]
  );

  const onEditAddress = useCallback(
    (error?: FedExError) => {
      history.push("./0");
      dispatch({
        data: {
          addressError: error,
        },
      });
    },
    [history]
  );

  const onAddressValidated = useCallback(
    (data: {
      originalAddress: FedExPickUpAddress;
      normalizedAddress: FedExPickUpAddress;
      shipperDetails: FedExShipperDetails;
    }) => {
      history.push("./1");
      dispatch({
        data: {
          proposedAddresses: {
            original: data.originalAddress,
            normalized: data.normalizedAddress,
          },
          shipperDetails: data.shipperDetails,
          addressError: undefined,
        },
      });
    },
    [history]
  );

  const onPickupScheduled = useCallback(
    async (
      pickupWindow: ProposedFedExPickupTime,
      packageLocation: FedExDropLocation,
      remarks: string,
      confirmation: string
    ) => {
      await appDispatch(setScheduledPickup({ confirmation, pickupWindow }));
      history.push("./0");
      appDispatch(
        confirmPickup({
          address: shippingState.data.address!,
          packageLocation,
          remarks,
        })
      );
    },
    [history, appDispatch, shippingState.data.address]
  );

  if (!testRunState.subscribed) {
    // Don't flash the address form while we load the testRun
    return null;
  }

  switch (page) {
    case "ENTER_ADDRESS":
      return (
        <FedExAddressForm
          addressError={shippingState.data.addressError}
          onAddressValidated={onAddressValidated}
          initialAddress={shippingState.data.proposedAddresses?.original}
          initialShipperDetails={shippingState.data.shipperDetails}
        />
      );
    case "SELECT_ADDRESS":
      return (
        <FedExSelectAddress
          normalizedAddress={shippingState.data.proposedAddresses!.normalized}
          originalAddress={shippingState.data.proposedAddresses!.original}
          onEditAddress={onEditAddress}
          onReceivePickupTimes={onReceivePickupTimes}
          shipperDetails={shippingState.data.shipperDetails!}
        />
      );
    case "SELECT_TIME":
      return (
        <FedExSelectTime
          address={shippingState.data.address!}
          shipperDetails={shippingState.data.shipperDetails!}
          proposedTimes={shippingState.data.proposedTimes!}
          onPickupScheduled={onPickupScheduled}
          onEditAddress={onEditAddress}
        />
      );
    case "CONFIRMATION":
      return (
        <FedExConfirmationPage
          confirmation={confirmation!}
          details={confirmationDetails}
        />
      );
  }
};
